import { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, AlertTriangle, CheckCircle2, Wand2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = {
  MISSED_PUNCH_OUT: { short: "Missed Punch Out", color: "red" },
  MISSED_PUNCH_IN: { short: "Missed Punch In", color: "red" },
  SHORT_DAY: { short: "Short Day (<4h)", color: "amber" },
  OVER_16_HOURS: { short: "Over 16 hrs", color: "amber" },
};

const REASON_OPTIONS = [
  "Forgot to punch out — confirmed verbally",
  "Forgot to punch in — confirmed verbally",
  "Machine error — verified by supervisor",
  "Approved early leave",
];

const ddmmyyyy = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}-${m}-${y}`;
};
const dayName = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { weekday: "long" });
};

export default function AttendanceAnomalies() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ counts: { total: 0, blocking_unresolved: 0, warning_unresolved: 0, resolved: 0, by_type: {} }, rows: [] });
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("unresolved");
  const [search, setSearch] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [busy, setBusy] = useState(false);

  // Single-row correction dialog
  const [editing, setEditing] = useState(null); // a row object
  const [correctIn, setCorrectIn] = useState("");
  const [correctOut, setCorrectOut] = useState("");
  const [reason, setReason] = useState(REASON_OPTIONS[0]);
  const [reasonOther, setReasonOther] = useState("");

  // Bulk-apply dialog
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkType, setBulkType] = useState("MISSED_PUNCH_OUT");
  const [bulkLastOut, setBulkLastOut] = useState("");
  const [bulkFirstIn, setBulkFirstIn] = useState("");
  const [bulkReason, setBulkReason] = useState("Bulk fix from anomaly review");
  const [bulkCategory, setBulkCategory] = useState("any");
  const [bulkShiftHours, setBulkShiftHours] = useState("any");

  // Rev. 2026-08-04 — Bulk-accept warnings (Over-16-hrs, Short-Day) as-is
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [acceptType, setAcceptType] = useState("OVER_16_HOURS");
  const [acceptReason, setAcceptReason] = useState("Overnight shift — verified by HR");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType !== "all") params.anomaly_type = filterType;
      if (filterStatus === "resolved") params.only_resolved = true;
      if (filterStatus === "unresolved") params.only_unresolved = true;
      if (filterDate) params.date = filterDate;
      const { data: res } = await api.get(`/attendance/uploads/${id}/anomalies`, { params });
      setData(res);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, filterType, filterStatus, filterDate]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return data.rows;
    const q = search.trim().toLowerCase();
    return data.rows.filter((r) =>
      String(r.employee_name || "").toLowerCase().includes(q) ||
      String(r.punch_id || "").toLowerCase().includes(q) ||
      String(r.employee_code || "").toLowerCase().includes(q)
    );
  }, [data.rows, search]);

  const openCorrect = (row) => {
    setEditing(row);
    setCorrectIn(row.first_in || "");
    setCorrectOut(row.last_out || "");
    const t = row.anomaly_type;
    if (t === "MISSED_PUNCH_IN") setReason(REASON_OPTIONS[1]);
    else setReason(REASON_OPTIONS[0]);
    setReasonOther("");
  };

  const submitCorrection = async (markAbsent = false) => {
    if (!editing) return;
    const reasonFinal = reason === "Other" ? reasonOther.trim() : reason;
    if (!reasonFinal) {
      toast.error("Reason is required");
      return;
    }
    if (!markAbsent) {
      if (!/^\d{2}:\d{2}$/.test(correctIn) || !/^\d{2}:\d{2}$/.test(correctOut)) {
        toast.error("Both times must be in HH:MM 24-hour format");
        return;
      }
    }
    setBusy(true);
    try {
      await api.post(`/attendance/daily/${editing.id}/correct`, {
        first_in: markAbsent ? null : correctIn,
        last_out: markAbsent ? null : correctOut,
        reason: reasonFinal,
        mark_absent: markAbsent,
      });
      toast.success(markAbsent ? "Marked as Absent" : "Correction saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submitBulk = async () => {
    if (!bulkLastOut && !bulkFirstIn) {
      toast.error("Provide at least one time");
      return;
    }
    if (!bulkReason.trim()) {
      toast.error("Reason is required");
      return;
    }
    if (!window.confirm(`Apply this correction to all matching unresolved anomalies?`)) return;
    setBusy(true);
    try {
      const payload = {
        anomaly_type: bulkType,
        reason: bulkReason.trim(),
      };
      if (bulkLastOut) payload.last_out = bulkLastOut;
      if (bulkFirstIn) payload.first_in = bulkFirstIn;
      if (bulkCategory !== "any") payload.category = bulkCategory;
      if (bulkShiftHours !== "any") payload.shift_hours = parseFloat(bulkShiftHours);
      const { data: res } = await api.post(`/attendance/uploads/${id}/anomalies/bulk-apply`, payload);
      toast.success(`Applied to ${res.applied} anomalies`);
      setBulkOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // Rev. 2026-08-04 — accept ALL matching warnings as-is
  const submitAcceptWarnings = async () => {
    const reasonTxt = acceptReason.trim();
    if (!reasonTxt) {
      toast.error("Reason is required");
      return;
    }
    const typeLabel = acceptType === "ALL_WARNINGS"
      ? "all warning anomalies"
      : (TYPE_LABELS[acceptType]?.short || acceptType);
    const estimate = acceptType === "ALL_WARNINGS"
      ? (c.warning_unresolved || 0)
      : (c.by_type?.[acceptType] || 0);
    if (!window.confirm(
      `Accept ${estimate} ${typeLabel} without changing punch times?\n\n` +
      `They will be marked as resolved with reason:\n"${reasonTxt}"\n\nProceed?`,
    )) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(
        `/attendance/uploads/${id}/anomalies/bulk-accept-warnings`,
        { anomaly_type: acceptType, reason: reasonTxt },
      );
      toast.success(`Accepted ${res.accepted} warning${res.accepted === 1 ? "" : "s"}`);
      setAcceptOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (loading && data.rows.length === 0) {
    return <div className="text-sm text-slate-500" data-testid="anomaly-loading">Loading anomalies…</div>;
  }
  const c = data.counts;

  return (
    <div data-testid="anomaly-page" className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to={`/attendance/review/${id}`} className="text-slate-500 hover:text-slate-900">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Punch anomaly review</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Resolve missing punches
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="bulk-fix-btn"
            variant="outline"
            onClick={() => setBulkOpen(true)}
            disabled={c.blocking_unresolved === 0}
          >
            <Wand2 className="h-4 w-4 mr-2" />
            Bulk fix
          </Button>
          <Button
            data-testid="bulk-accept-warnings-btn"
            variant="outline"
            onClick={() => setAcceptOpen(true)}
            disabled={c.warning_unresolved === 0}
            className="border-green-300 text-green-800 hover:bg-green-50"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Accept all warnings
          </Button>
          <Button
            data-testid="back-to-review-btn"
            onClick={() => navigate(`/attendance/review/${id}`)}
            className="brand-bg brand-bg-hover text-white"
            disabled={c.blocking_unresolved > 0}
          >
            {c.blocking_unresolved > 0
              ? `${c.blocking_unresolved} blocker${c.blocking_unresolved === 1 ? "" : "s"} left`
              : "Back to review"}
          </Button>
        </div>
      </div>

      {c.blocking_unresolved > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-800 flex items-center gap-3" data-testid="blocking-banner">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <div>
            <div className="font-semibold">
              {c.blocking_unresolved} punch anomal{c.blocking_unresolved === 1 ? "y" : "ies"} need{c.blocking_unresolved === 1 ? "s" : ""} review before payroll can be processed.
            </div>
            <div className="text-xs text-red-700 mt-0.5">Correct the times below or mark days as Absent.</div>
          </div>
        </div>
      )}
      {c.blocking_unresolved === 0 && c.total > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 text-sm text-green-800 flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
          <div>All blocking anomalies resolved. {c.warning_unresolved} warning{c.warning_unresolved === 1 ? "" : "s"} can be reviewed (optional).</div>
        </div>
      )}
      {c.total === 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-8 text-center text-sm text-slate-500" data-testid="no-anomalies">
          No anomalies found in this upload.
        </div>
      )}

      {/* Counter chips */}
      {c.total > 0 && (
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <Badge variant="outline" className="bg-red-50 border-red-200 text-red-800">
            {c.blocking_unresolved} blocker{c.blocking_unresolved === 1 ? "" : "s"}
          </Badge>
          <Badge variant="outline" className="bg-amber-50 border-amber-200 text-amber-800">
            {c.warning_unresolved} warning{c.warning_unresolved === 1 ? "" : "s"}
          </Badge>
          <Badge variant="outline" className="bg-green-50 border-green-200 text-green-800">
            {c.resolved} resolved
          </Badge>
          <span className="text-slate-400 ml-1">/</span>
          <span className="text-slate-500">{c.total} total</span>
        </div>
      )}

      {/* Filter bar */}
      {c.total > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-4 flex items-center gap-3 flex-wrap">
          <div className="flex flex-col gap-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Type</Label>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-56" data-testid="filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {Object.entries(TYPE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.short} ({c.by_type?.[k] || 0})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Status</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-44" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unresolved">Unresolved</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Date</Label>
            <Input
              data-testid="filter-date"
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="w-44"
            />
          </div>
          <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Search</Label>
            <Input
              data-testid="filter-search"
              placeholder="Punch ID, code, or name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Anomalies table */}
      {c.total > 0 && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Employee</th>
                <th className="text-left px-3 py-2 font-medium">Date</th>
                <th className="text-left px-3 py-2 font-medium">Type</th>
                <th className="text-left px-3 py-2 font-medium">First IN</th>
                <th className="text-left px-3 py-2 font-medium">Last OUT</th>
                <th className="text-right px-3 py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center py-10 text-slate-400 text-xs">
                    No anomalies match the current filters.
                  </td>
                </tr>
              )}
              {filteredRows.map((r) => {
                const t = TYPE_LABELS[r.anomaly_type] || { short: r.anomaly_type, color: "slate" };
                const isResolved = !!r.anomaly_resolved;
                const isBlocker = !!r.anomaly_blocking;
                return (
                  <tr key={r.id} data-testid={`anomaly-row-${r.id}`} className={isResolved ? "bg-green-50/30" : ""}>
                    <td className="px-3 py-2">
                      {isResolved ? (
                        <span className="text-green-700 text-xs flex items-center gap-1">
                          <CheckCircle2 className="h-3.5 w-3.5" />resolved
                        </span>
                      ) : isBlocker ? (
                        <span className="text-red-600 text-xs">● blocker</span>
                      ) : (
                        <span className="text-amber-600 text-xs">● warning</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{r.employee_name}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{r.punch_id} · {r.employee_code}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="font-medium">{ddmmyyyy(r.date)}</div>
                      <div className="text-[11px] text-slate-500">{dayName(r.date)}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`inline-block px-1.5 py-0.5 rounded bg-${t.color}-100 text-${t.color}-800 font-medium`}>
                        {t.short}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {r.first_in || <span className="text-red-500">missing</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {r.last_out || <span className="text-red-500">missing</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        data-testid={`correct-btn-${r.id}`}
                        size="sm"
                        variant={isResolved ? "outline" : "default"}
                        className={isResolved ? "" : "brand-bg brand-bg-hover text-white"}
                        onClick={() => openCorrect(r)}
                      >
                        {isResolved ? "Re-edit" : "Correct"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Single correction dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="correct-dialog">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle>
                  Correct punch — {editing.employee_name} · {ddmmyyyy(editing.date)}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs space-y-1">
                  <div className="text-slate-500 uppercase tracking-wider text-[10px]">Recorded by machine</div>
                  <div>First IN: <span className="font-mono">{editing.original_first_in || editing.first_in || "—"}</span></div>
                  <div>Last OUT: <span className="font-mono">{editing.original_last_out || editing.last_out || "—"}</span></div>
                  <div className="text-red-700 mt-1">{editing.anomaly_reason}</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-slate-500">First IN</Label>
                    <Input
                      data-testid="correct-first-in"
                      placeholder="HH:MM"
                      value={correctIn}
                      onChange={(e) => setCorrectIn(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-slate-500">Last OUT</Label>
                    <Input
                      data-testid="correct-last-out"
                      placeholder="HH:MM"
                      value={correctOut}
                      onChange={(e) => setCorrectOut(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-slate-500">Reason</Label>
                  <Select value={reason} onValueChange={setReason}>
                    <SelectTrigger data-testid="correct-reason"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {REASON_OPTIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      <SelectItem value="Other">Other (specify)</SelectItem>
                    </SelectContent>
                  </Select>
                  {reason === "Other" && (
                    <Textarea
                      data-testid="correct-reason-other"
                      className="mt-2"
                      rows={2}
                      placeholder="Specify reason"
                      value={reasonOther}
                      onChange={(e) => setReasonOther(e.target.value)}
                    />
                  )}
                </div>
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                <Button
                  data-testid="mark-absent-btn"
                  variant="outline"
                  onClick={() => submitCorrection(true)}
                  disabled={busy}
                >
                  Mark as Absent
                </Button>
                <Button
                  data-testid="save-correction-btn"
                  onClick={() => submitCorrection(false)}
                  className="brand-bg brand-bg-hover text-white"
                  disabled={busy}
                >
                  Save correction
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk-apply dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="bulk-dialog">
          <DialogHeader>
            <DialogTitle>Bulk fix punch anomalies</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-xs text-slate-500">
              Apply a single time correction to every unresolved anomaly that matches the filters below. Useful for "all admin staff who forgot to punch out at 18:30".
            </p>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Anomaly type</Label>
              <Select value={bulkType} onValueChange={setBulkType}>
                <SelectTrigger data-testid="bulk-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="MISSED_PUNCH_OUT">Missed Punch Out</SelectItem>
                  <SelectItem value="MISSED_PUNCH_IN">Missed Punch In</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Category filter</Label>
                <Select value={bulkCategory} onValueChange={setBulkCategory}>
                  <SelectTrigger data-testid="bulk-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="white_collar">White Collar</SelectItem>
                    <SelectItem value="white_collar_female">White Collar - Female</SelectItem>
                    <SelectItem value="blue_collar">Blue Collar</SelectItem>
                    <SelectItem value="driver">Drivers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Shift hours filter</Label>
                <Select value={bulkShiftHours} onValueChange={setBulkShiftHours}>
                  <SelectTrigger data-testid="bulk-shift"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="8.5">8.5 hrs</SelectItem>
                    <SelectItem value="9">9.0 hrs</SelectItem>
                    <SelectItem value="9.5">9.5 hrs</SelectItem>
                    <SelectItem value="12">12 hrs</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">First IN (optional)</Label>
                <Input
                  data-testid="bulk-first-in"
                  placeholder="HH:MM (leave blank to keep)"
                  value={bulkFirstIn}
                  onChange={(e) => setBulkFirstIn(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Last OUT (optional)</Label>
                <Input
                  data-testid="bulk-last-out"
                  placeholder="HH:MM (leave blank to keep)"
                  value={bulkLastOut}
                  onChange={(e) => setBulkLastOut(e.target.value)}
                />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Reason</Label>
              <Textarea
                data-testid="bulk-reason"
                rows={2}
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button
              data-testid="bulk-apply-btn"
              onClick={submitBulk}
              className="brand-bg brand-bg-hover text-white"
              disabled={busy}
            >
              Apply to all matches
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rev. 2026-08-04 — Bulk Accept Warnings dialog */}
      <Dialog open={acceptOpen} onOpenChange={setAcceptOpen}>
        <DialogContent className="max-w-md" data-testid="bulk-accept-dialog">
          <DialogHeader>
            <DialogTitle>Accept warnings as-is</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="rounded-md bg-green-50 border border-green-200 p-3 text-green-900 text-xs">
              For legitimate overnight shifts (9:00 → 1:30 next day) etc., use
              this to bulk-mark warnings as reviewed <span className="font-semibold">without changing</span>
              {" "}the recorded punch times. Only warnings (Over 16 hrs, Short Day)
              can be accepted this way — blocking anomalies must still be fixed
              individually or via Bulk fix.
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                Anomaly type
              </Label>
              <Select value={acceptType} onValueChange={setAcceptType}>
                <SelectTrigger data-testid="accept-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL_WARNINGS">
                    All warnings ({c.warning_unresolved})
                  </SelectItem>
                  <SelectItem value="OVER_16_HOURS">
                    Over 16 hrs ({c.by_type?.OVER_16_HOURS || 0})
                  </SelectItem>
                  <SelectItem value="SHORT_DAY">
                    Short Day &lt; 4h ({c.by_type?.SHORT_DAY || 0})
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                Reason (recorded in audit log)
              </Label>
              <Textarea
                data-testid="accept-reason"
                value={acceptReason}
                onChange={(e) => setAcceptReason(e.target.value)}
                rows={3}
                placeholder="Overnight shift — verified by HR"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAcceptOpen(false)}>Cancel</Button>
            <Button
              data-testid="bulk-accept-btn"
              onClick={submitAcceptWarnings}
              className="bg-green-600 hover:bg-green-700 text-white"
              disabled={busy}
            >
              <CheckCircle2 className="h-4 w-4 mr-1.5" />
              Accept all
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
